import { useQuery, useSubscription } from '@apollo/client';
import { useSetAtom } from 'jotai';
import Quill, { Delta } from 'quill/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import Page from '../app/layout/Page';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Toast from '../components/Toast';
import {
  ACTION_EDITOR_RENDER_DURATION,
  ACTION_TYPING_APDEX,
  CATEGORY_PRODUCT,
} from '../constants/analytics';
import { GRANT_ACCESS_TO } from '../constants/params';
import useTestHelpersInDev from '../dev/useTestHelpersInDev';
import type {
  PerformanceMeasureHandler,
  TextChangeHandler,
} from '../editor/Editor';
import Editor, { EditorContainer } from '../editor/Editor';
import QuillContext, { useQuill } from '../editor/QuillContext';
import UIElementResizeContext from '../editor/UIElementResizeContext';
import WiderLineSizeContext from '../editor/WiderLineSizeContext';
import { MentionPickerProvider } from '../editor/controls/MentionPicker';
import { EditorActions } from '../editor/editorSlice';
import useAnchorScrollingHandler, {
  scrollAnchorIntoView,
} from '../editor/useAnchorScrollingHandler';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { getSearchContext } from '../search/searchContext';
import { usePostSeries } from '../series/useAllSeries';
import InternalTopbar from '../topbar/InternalTopbar';
import { useSavePostToRecentlyViewed } from '../topbar/SearchBar';
import { UIActions } from '../ui/uiSlice';
import { useAllUsersMap, useGetUser } from '../user/useAllUsers';
import Logger from '../utils/Logger';
import PageMeta from '../utils/PageMeta';
import { track } from '../utils/analytics';
import useErrorRedirectEffect from '../utils/error/useErrorRedirectEffect';
import Sizes, {
  LayoutSize,
  RIGHT_PANEL_WIDTH,
} from '../utils/responsive/Sizes';
import { useIsSize } from '../utils/responsive/isSize';
import { responsiveLayoutSize } from '../utils/responsive/useIsLayoutSize';
import useMainContentHistory from '../utils/router/useMainContentHistory';
import useNavigate from '../utils/router/useNavigate';
import useSlug from '../utils/router/useSlug';
import { getThreadIdFromUrlHash } from '../utils/url/commentThreadHash';
import postPath from '../utils/url/postPath';
import ControlBar, { FooterControlBar } from './ControlBar';
import HoverContentPreview from './HoverContentPreview';
import MagneticWall from './MagneticWall';
import PostActivityTracker from './PostActivityTracker';
import * as PostLayout from './PostLayout';
import PostLayoutProvider from './PostLayoutProvider';
import { POST_SIDEBAR_SHIFT_BREAKPOINT } from './PostLayoutSizes';
import PostSaveTracker from './PostSaveTracker';
import PostSeriesBar from './PostSeriesBar';
import PostSkeleton from './PostSkeleton';
import PostStateBar from './PostStateBar';
import PostStickyControl from './PostStickyControl';
import * as styles from './PostView.module.scss';
import PostViewBannerEditor from './PostViewBannerEditor';
import { contentContainsCommentThread } from './comment/threadHelpers';
import getVerificationStatus, {
  VerificationStatus,
} from './getVerificationStatus';
import {
  isVerifier,
  PostPolicyAction,
  usePostPolicyCheck,
} from './postPolicyCheck';
import PostActions from './postView/PostActions';
import { isShareDropdownOpenAtom } from './postView/PostEditButton';
import PostSeriesFooter from './series/PostSeriesFooter';
import { useAddPostUser } from './sharing/postPermissionOperations';
import PostTopicBar from './topic/PostTopicBar';
import useCachedPostTitle from './useCachedPostTitle';
import { isDeletedLocally } from './useDeletePost';
import useHasMagneticWallItems from './useHasMagneticWallItems';
import useInactivityObserver from './useInactivityObserver';
import usePostChromeVisible from './usePostChromeVisible';
import useRequestPostAccess from './useRequestPostAccess';
import useShareSecret from './useShareSecret';
import useSyncShareSecretWithUrl from './useSyncShareSecretWithUrl';

const POST_QUERY = gql(/** @complexity 41 */ `
  query PostView(
    $id: ID!
    $queryId: String
    $resultId: String
    $position: Int
    $shareSecret: String
  ) {
    post(
      id: $id
      view: true
      queryId: $queryId
      resultId: $resultId
      position: $position
      shareSecret: $shareSecret
    ) {
      id
      externalPost {
        id
      }
      banner {
        original
      }
      verificationEntries {
        id
        verifier {
          id
          ...useGetUser_user
        }
      }
      ...PostTopicBar_post
      ...Editor_post
      ...usePostPolicyCheck_post
      ...MagneticWall_post
      ...useSavePostToRecentlyViewed_post
      ...PostViewBannerEditor_post
      ...EditorActions_post
      ...getVerificationStatus_post
      ...isVerifier_post
      ...postPath_post
      ...ControlBar_post
      ...useHasMagneticWallItems_post
      ...useSyncShareSecretWithUrl_post
      ...FooterControlBar_post
      ...PostActions_post
      ...PostStateBar_post
      ...MentionPickerProvider_post
    }
  }
`);

const POST_UPDATED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription postUpdate($postId: ID!) {
    postUpdated(postId: $postId) {
      id
      publishedAt
      archivedAt
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const POST_DELETED_SUBSCRIPTION = gql(/** @complexity 32 */ `
  subscription postDeleted($postId: ID!) {
    post: postDeleted(postId: $postId) {
      ...PostSummaryFields
    }
  }
`);

const MAX_TRACKING_EVENT_COUNT = 20;
const useTypingPerformanceObserver = (postId: string) => {
  const { quill } = useQuill();
  const [shouldObserve, setShouldObserve] = useState(true);

  useEffect(() => {
    if (!quill || !shouldObserve) return undefined;

    const isEventTimingAPISupported =
      'PerformanceObserver' in window &&
      typeof PerformanceObserver === 'function' &&
      PerformanceObserver.supportedEntryTypes?.includes('event');

    if (!isEventTimingAPISupported) return undefined;

    // We use timestamp for event identifier to link a PerformanceObserver entry to a keydown event
    // https://github.com/w3c/event-timing/issues/108
    const eventTimeStamps = new Set<number>();
    const slowDurations: number[] = [];
    const alphanumericRegex = /^[a-zA-Z0-9]$/;

    let lastObserverEntryWaitingTimer: ReturnType<typeof setTimeout> | null =
      null;

    const handleKeydown = (e: KeyboardEvent) => {
      if (lastObserverEntryWaitingTimer) return;

      // Don't track shortcuts or Enter/Backspace keys because their performance varies a lot and
      // can make the Apdex less accurate.
      const shouldTrackPerformance =
        !e.isComposing &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.repeat &&
        alphanumericRegex.test(e.key);

      if (!shouldTrackPerformance) {
        return;
      }

      if (eventTimeStamps.size < MAX_TRACKING_EVENT_COUNT) {
        eventTimeStamps.add(e.timeStamp);
        return;
      }

      // Wait for PerformanceObserver to collect data for the last keydown event
      lastObserverEntryWaitingTimer = setTimeout(() => {
        setShouldObserve(false); // We already have enough data

        const frustratedDuration = 400;
        const frustratedCount = slowDurations.filter(
          (d) => d >= frustratedDuration,
        ).length;
        const toleratingCount = slowDurations.length - frustratedCount;
        const satisfiedCount = MAX_TRACKING_EVENT_COUNT - slowDurations.length;
        const apdex =
          (satisfiedCount + toleratingCount / 2) / MAX_TRACKING_EVENT_COUNT;

        track(ACTION_TYPING_APDEX, {
          category: CATEGORY_PRODUCT,
          label: postId,
          value: apdex,
        });

        if (apdex < 0.5) {
          Logger.warn('Slow typing warning', { postId, apdex });
        }
      }, 2000);
    };
    quill.root.addEventListener('keydown', handleKeydown);

    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.name === 'keydown' && eventTimeStamps.has(entry.startTime)) {
          slowDurations.push(entry.duration);
        }
      });
    });

    observer.observe({ type: 'event' });
    return () => {
      quill.root.removeEventListener('keydown', handleKeydown);
      observer.disconnect();
      if (lastObserverEntryWaitingTimer) {
        clearTimeout(lastObserverEntryWaitingTimer);
      }
    };
  }, [quill, shouldObserve, postId]);
};

const usePostDeleteSubscription = (postId: string) => {
  const dispatch = useAppDispatch();
  const mainContentHistory = useMainContentHistory();
  const handleSubscriptionData = () => {
    if (isDeletedLocally(postId)) return;

    dispatch(UIActions.removeFromRecentlyViewedPostIds(postId));
    dispatch(
      UIActions.pushToast((toastProps) => (
        <Toast
          level="error"
          message="The post you were viewing was just deleted."
          onDismiss={toastProps.onDismiss}
          title="Oops"
        />
      )),
    );
    setTimeout(() => {
      mainContentHistory.back((route) => !route.pathname.includes(postId), {
        replace: true,
      });
    }, 0);
  };

  useSubscription(POST_DELETED_SUBSCRIPTION, {
    variables: { postId },
    onData: handleSubscriptionData,
  });
};

function useCommentHistoryRedirect(
  post?: DocumentType<typeof POST_QUERY>['post'],
) {
  const { quill } = useQuill();
  const navigate = useNavigate();

  const calculatedPostPath = post ? postPath(post) : null;

  useEffect(() => {
    const { hash } = document.location;
    const threadId = getThreadIdFromUrlHash(hash);

    if (
      calculatedPostPath &&
      threadId != null &&
      quill &&
      !contentContainsCommentThread(quill.editor.delta, threadId)
    ) {
      navigate(`${calculatedPostPath}/comments${hash}`, { replace: true });
    }
  }, [calculatedPostPath, navigate, quill]);
}

interface PostViewProps {
  postId: string;
}

const PostView = ({ postId }: PostViewProps) => {
  const dispatch = useAppDispatch();
  const shareSecret = useShareSecret();
  const originalShareSecretRef = useRef(shareSecret);

  useEffect(
    () => () => {
      dispatch(EditorActions.leave());
    },
    [dispatch, postId],
  );

  const isMobile = useIsSize(Sizes.SM);
  const isChromeVisible = usePostChromeVisible({ enabled: isMobile });
  const { quill } = useQuill();
  useTestHelpersInDev();
  useTypingPerformanceObserver(postId);

  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const isVersionLoaded = useAppSelector((state) => state.editor.version > -1);

  const cachedTitle = useCachedPostTitle(postId);
  const title = useAppSelector((state) => state.editor.title) || cachedTitle;

  const isLocked = useAppSelector(
    (state) => state.session.organization.isLocked,
  );
  const currentUser = useAppSelector((state) => state.session.user);
  const channel = useAppSelector((state) => state.editor.channel);
  useInactivityObserver(channel);

  const { queryId, resultId, position, highlightedRangeIndex } = useMemo(
    () => getSearchContext(postId),
    [postId],
  );

  const postLayoutContainerRef = useRef<HTMLElement | null>(null);
  const postLayoutBannerRef = useRef<HTMLDivElement | null>(null);
  const innerBannerRef = useRef<HTMLDivElement | null>(null);

  // Remote updates not applied yet
  const receivedRef = useRef(new Delta());

  useEffect(
    () => () => {
      dispatch(UIActions.closeRightPanel({ reset: true }));
    },
    [dispatch, postId],
  );

  const { data, error, loading } = useQuery(POST_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      id: postId,
      queryId,
      resultId,
      position,
      shareSecret: originalShareSecretRef.current,
    },
  });

  const post = data?.post;

  useSyncShareSecretWithUrl(post);
  useCommentHistoryRedirect(post);
  usePostDeleteSubscription(postId);
  useSubscription(POST_UPDATED_SUBSCRIPTION, { variables: { postId } });

  const { requestAccess } = useRequestPostAccess({ postId });

  const handleRequestAccess = async (onDismiss: () => void) => {
    await requestAccess();
    onDismiss();
    setTimeout(() => {
      dispatch(
        UIActions.pushToast({ level: 'success', message: 'Access requested' }),
      );
    }, 250); // Wait until the first (wider) toast is dismissed to not change the layout
  };

  useErrorRedirectEffect({
    error,
    noun: 'post',
    toastProps: {
      message:
        'You do not have permission to view this post. Request access from the owner?',
      primaryText: 'Request access',
      onPrimaryClick: ({ onDismiss }) => {
        handleRequestAccess(onDismiss);
      },
      secondaryText: 'No, thanks',
      onSecondaryClick: ({ onDismiss }) => {
        onDismiss();
      },
    },
  });

  useSavePostToRecentlyViewed(post);

  const quillRef = useRef(quill);
  useEffect(() => {
    if (!quillRef.current && quill && document.location.hash) {
      scrollAnchorIntoView(quill, document.location.hash);
    }

    quillRef.current = quill;
    return () => {
      quillRef.current = null;
    };
  }, [quill]);

  const series = usePostSeries(postId);

  const postPolicyCheck = usePostPolicyCheck();

  const isPostEditable =
    !!post &&
    !isLocked &&
    postPolicyCheck(PostPolicyAction.EditPost, post, currentUser) &&
    post.externalPost == null;

  const loadedPostIdRef = useRef<string | null>(null);
  const loadedIsPostEditableRef = useRef<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userMap = useAllUsersMap();
  const getUser = useGetUser();
  const setIsShareDropdownOpen = useSetAtom(isShareDropdownOpenAtom);
  const addUser = useAddPostUser();

  const verificationStatus = post ? getVerificationStatus(post) : null;

  const isCurrentUserVerifier = post ? isVerifier(post, currentUser) : false;

  const verifier = post?.verificationEntries[0]?.verifier
    ? getUser(post.verificationEntries[0].verifier)
    : null;

  useEffect(() => {
    const { verification, ...locationState } = location.state ?? {};
    if (verification && verificationStatus) {
      if (
        verifier &&
        verifier.id !== currentUser.id &&
        isCurrentUserVerifier &&
        verificationStatus === VerificationStatus.Verified
      ) {
        dispatch(
          UIActions.pushToast({
            level: 'success',
            message: `${verifier.name} has already verified this post!`,
          }),
        );
      }
      navigate(location, { replace: true, state: locationState });
    }
  }, [
    currentUser.id,
    dispatch,
    isCurrentUserVerifier,
    location,
    navigate,
    verifier,
    verificationStatus,
    getUser,
  ]);

  useEffect(() => {
    if (
      post &&
      !error &&
      !loading &&
      (loadedPostIdRef.current !== post.id ||
        loadedIsPostEditableRef.current !== isPostEditable)
    ) {
      dispatch(
        EditorActions.load({
          post,
          isEditable: isPostEditable,
          isPublicView: false,
          isDefaultReadOnly: responsiveLayoutSize() === LayoutSize.mobile,
          onRemoteDelta: (delta: Delta) => {
            receivedRef.current = receivedRef.current.compose(delta);
            quillRef.current?.updateContents(
              receivedRef.current,
              Quill.sources.API,
            );
          },
          navigate,
        }),
      );

      if (location.state?.edit) {
        dispatch(EditorActions.readOnly(false));
        navigate(location, {
          replace: true,
          state: { edit: false, editTopic: false },
        });
      }

      loadedPostIdRef.current = post.id;
      loadedIsPostEditableRef.current = isPostEditable;
    }

    return undefined;
  }, [dispatch, error, isPostEditable, loading, navigate, post, location]);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const grantAccessTo = searchParams.get(GRANT_ACCESS_TO);
    const user = grantAccessTo ? userMap[grantAccessTo] : null;

    if (user && post) {
      setSearchParams({}, { replace: true });

      if (postPolicyCheck(PostPolicyAction.ViewPost, post, user)) {
        return;
      }

      addUser(post, user.id);
      dispatch(
        UIActions.pushToast((toastProps) => (
          <Toast
            level="success"
            message={
              <>
                Post shared with user.{' '}
                <Button
                  variant="link"
                  onClick={() => {
                    setIsShareDropdownOpen(true);
                    toastProps.onDismiss();
                  }}
                >
                  View sharing
                </Button>
              </>
            }
            onDismiss={toastProps.onDismiss}
          />
        )),
      );
    }
  }, [
    addUser,
    dispatch,
    post,
    postPolicyCheck,
    navigate,
    setIsShareDropdownOpen,
    userMap,
    searchParams,
    setSearchParams,
  ]);

  const anchorScrollingHandlerProps = useAnchorScrollingHandler(quill, postId, {
    updateLocationHash: true,
  });
  const handleTextChange: TextChangeHandler = useCallback(
    (_delta, _oldContent, source) => {
      if (source === Quill.sources.API) {
        receivedRef.current = new Delta();
      }
    },
    [],
  );

  const handlePerformanceMeasure: PerformanceMeasureHandler = useCallback(
    (durations) => {
      track(ACTION_EDITOR_RENDER_DURATION, {
        category: CATEGORY_PRODUCT,
        label: postId,
        ...durations,
      });
    },
    [postId],
  );

  const currentBannerUrl = post?.banner?.original ?? null;
  const currentPostBanner = useAppSelector(
    (state) => state.editor.currentPostBanner,
  );
  const hasStateBar = useAppSelector((state) => state.editor.hasStateBar);

  const banner =
    currentPostBanner ??
    (currentBannerUrl ? { image: currentBannerUrl } : null);

  const postLayoutTocRef = useRef<HTMLElement | null>(null);
  const postLayoutMagneticWallRef = useRef<HTMLElement | null>(null);
  const [gridItemForStateBar, setGridItemForStateBar] =
    useState<HTMLElement | null>(null);

  const isEditing =
    !readOnly ||
    (post != null &&
      !isLocked &&
      postPolicyCheck(PostPolicyAction.EditPost, post, currentUser) &&
      post.externalPost != null);

  const hasMagneticWallItems = useHasMagneticWallItems(post);

  return (
    <Page
      topbar={
        <InternalTopbar postActions={post && <PostActions post={post} />} />
      }
    >
      <PostLayoutProvider hasMagneticWallItems={hasMagneticWallItems}>
        <PostViewContainer {...anchorScrollingHandlerProps}>
          {post && gridItemForStateBar && (
            <PostStateBar
              gridItem={gridItemForStateBar}
              post={post}
              hidden={!isChromeVisible}
              tocContainerRef={postLayoutTocRef}
              magneticWallContainerRef={postLayoutMagneticWallRef}
            />
          )}
          <PageMeta title={`${title || 'Post'} - Slab`} />
          <PostViewContent>
            <PostLayout.Container
              ref={postLayoutContainerRef}
              hasBanner={banner != null}
              hasStateBar={hasStateBar}
            >
              {isVersionLoaded && post != null ? (
                <WiderLineSizeContext
                  containerRef={postLayoutContainerRef}
                  editor={quill?.scroll.domNode}
                >
                  <UIElementResizeContext>
                    <PostLayout.Above ref={setGridItemForStateBar}>
                      <PostTopicBar post={post} editing={isEditing} />
                      {series && (
                        <PostSeriesBar postId={post.id} series={series} />
                      )}
                    </PostLayout.Above>
                    {banner && (
                      <PostViewBannerEditor
                        setPostLayoutBannerRef={postLayoutBannerRef}
                        setInnerBannerRef={innerBannerRef}
                        banner={banner}
                        currentBannerUrl={currentBannerUrl}
                        post={post}
                      />
                    )}
                    <PostLayout.Editor>
                      <PostActivityTracker
                        postId={postId}
                        queryId={queryId}
                        resultId={resultId}
                      />
                      <PostSaveTracker postId={postId} />
                      <EditorContainer hasBanner={banner != null}>
                        <PostLayout.ControlBar>
                          <ControlBar post={post} />
                        </PostLayout.ControlBar>
                        <HoverContentPreview>
                          <MentionPickerProvider post={post}>
                            <Editor
                              post={post}
                              highlightedRangeIndex={highlightedRangeIndex}
                              onTextChange={handleTextChange}
                              onPerformanceMeasure={handlePerformanceMeasure}
                            />
                          </MentionPickerProvider>
                        </HoverContentPreview>
                      </EditorContainer>
                    </PostLayout.Editor>

                    <PostLayout.TOC ref={postLayoutTocRef}>
                      <PostStickyControl
                        postId={postId}
                        postLayoutBannerRef={postLayoutBannerRef}
                        postLayoutContainerRef={postLayoutContainerRef}
                        hasBanner={banner != null}
                      />
                    </PostLayout.TOC>

                    <PostLayout.MagneticWall
                      ref={postLayoutMagneticWallRef}
                      hasControlBar
                    >
                      {quill && (
                        <MentionPickerProvider post={post}>
                          <MagneticWall
                            quill={quill}
                            post={post}
                            canEditPost={isPostEditable}
                            innerBannerRef={innerBannerRef}
                            hasBanner={banner != null}
                          />
                        </MentionPickerProvider>
                      )}
                    </PostLayout.MagneticWall>
                    <PostLayout.Below>
                      <PostLayout.FooterControlBar>
                        <FooterControlBar post={post} />
                      </PostLayout.FooterControlBar>

                      {series && (
                        <div>
                          <PostSeriesFooter postId={postId} series={series} />
                          <PostSeriesBar postId={post.id} series={series} />
                        </div>
                      )}
                    </PostLayout.Below>
                  </UIElementResizeContext>
                </WiderLineSizeContext>
              ) : (
                <>
                  <PostLayout.Above />
                  <PostLayout.Editor>
                    <div className="post-layout-editor-inner">
                      <PostSkeleton />
                    </div>
                  </PostLayout.Editor>
                </>
              )}
            </PostLayout.Container>
          </PostViewContent>
        </PostViewContainer>
      </PostLayoutProvider>
    </Page>
  );
};

interface PostViewContainerProps {
  children: React.ReactNode;
}
export const PostViewContainer = ({
  children,
  ...props
}: PostViewContainerProps) => {
  return (
    <div className={styles.container} {...props}>
      {children}
    </div>
  );
};

interface PostViewContentProps {
  children: React.ReactNode;
}

export const PostViewContent = ({ children }: PostViewContentProps) => {
  const dispatch = useAppDispatch();
  const isRightPanelOpen = useAppSelector(
    (state) => state.ui.rightPanel.isOpen,
  );

  useEffect(() => {
    if (
      isRightPanelOpen &&
      window.innerWidth - RIGHT_PANEL_WIDTH < POST_SIDEBAR_SHIFT_BREAKPOINT
    ) {
      dispatch(UIActions.toggleSidebar(false));
    }
  }, [dispatch, isRightPanelOpen]);

  return (
    <div id="content" className="no-padding-x no-padding-y">
      {children}
    </div>
  );
};

const PostViewWrapper = () => {
  const [postId] = useSlug('postSlug');

  return (
    <QuillContext>
      <PostView key={postId} postId={postId} />
    </QuillContext>
  );
};

export default PostViewWrapper;
